/**
 * GuestGamePage.tsx
 *
 * Полная копия LP/lilla_AI2_rt.html с интеграцией guest mode
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useGameAnimation } from "./GameAnimationManager";
import GameBoard from "./GameBoard";
import Chat from "./Chat";
import Auth from "./Auth";
import DiceAnimationOverlay from "./DiceAnimationOverlay";
import { createGuestGame } from "../api";

const SNAKES = [
  { from: 12, to: 8 },
  { from: 16, to: 4 },
  { from: 24, to: 7 },
  { from: 29, to: 6 },
  { from: 44, to: 9 },
  { from: 52, to: 35 },
  { from: 55, to: 3 },
  { from: 61, to: 13 },
  { from: 63, to: 2 },
  { from: 72, to: 51 },
];

const LADDERS = [
  { from: 10, to: 23 },
  { from: 17, to: 69 },
  { from: 20, to: 32 },
  { from: 22, to: 60 },
  { from: 27, to: 41 },
  { from: 28, to: 50 },
  { from: 37, to: 66 },
  { from: 45, to: 67 },
  { from: 46, to: 62 },
  { from: 54, to: 68 },
];

const GuestGamePage: React.FC = () => {
  const { user } = useAuth();
  const { animationState } = useGameAnimation();

  const [gameId, setGameId] = useState<number | null>(null);
  const [currentCell, setCurrentCell] = useState<number | null>(null);
  const [playerName, setPlayerName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState("");
  const [showAuth, setShowAuth] = useState(false);
  const [guestInteractionsRemaining, setGuestInteractionsRemaining] = useState<number>(3);
  const [authReason, setAuthReason] = useState<string>("");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rtClickId = urlParams.get('rt_clickid');
    if (rtClickId) {
      localStorage.setItem('rt_clickid', rtClickId);
    }
  }, []);

  const handleCreateGuestGame = async () => {
    if (!playerName.trim()) {
      setMessage("Введите имя для начала пути");
      return;
    }

    setIsCreating(true);
    setMessage("");

    try {
      const result = await createGuestGame(playerName.trim());
      setGameId(result.game_id);
      setCurrentCell(result.current_cell);
      setMessage("");

      // Сохраняем guest_id для связывания после авторизации
      if (result.guest_id) {
        localStorage.setItem('guest_id', result.guest_id);
        localStorage.setItem('guest_game_id', result.game_id.toString());
        console.log('📦 Saved guest_id and game_id to localStorage:', result.guest_id, result.game_id);
      }

      setTimeout(() => {
        document.getElementById('game-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } catch (error: any) {
      setMessage(`Ошибка создания игры: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCellChange = (newCell: number) => {
    setCurrentCell(newCell);
  };

  const handleGuestLimitReached = (error: any) => {
    const errorDetail = error.data?.detail;
    let reason = "Для продолжения игры требуется авторизация";

    if (typeof errorDetail === 'object') {
      reason = errorDetail.message || reason;
    } else if (typeof errorDetail === 'string') {
      try {
        const parsed = JSON.parse(errorDetail.replace(/^401: /, ''));
        reason = parsed.message || reason;
      } catch {
        reason = errorDetail;
      }
    }

    setAuthReason(reason);
    setShowAuth(true);
  };

  if (showAuth) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        overflowY: 'auto'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          margin: 'auto'
        }}>
          {/* Header with auth reason */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            padding: '1.5rem',
            borderRadius: '20px 20px 0 0',
            color: 'white',
            textAlign: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>🔐 Требуется авторизация</h2>
            <p style={{
              marginTop: '0.75rem',
              fontSize: '1rem',
              opacity: 0.95,
              whiteSpace: 'pre-line',
              lineHeight: '1.4'
            }}>
              {authReason}
            </p>
          </div>

          {/* Standard Auth component */}
          <Auth onClose={() => setShowAuth(false)} />
        </div>
      </div>
    );
  }

  if (gameId) {
    return (
      <div className="container mt-3" id="game-section">
        <div className="row mb-3">
          <div className="col-12 text-center">
            <div className="alert alert-warning">
              <strong>🎯 Бесплатный режим:</strong> Осталось {guestInteractionsRemaining} из 3 бесплатных ходов
              <br />
              <small className="text-muted">Войдите после, чтобы сохранить прогресс и продолжить</small>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 text-center mb-4">
            <div className="alert alert-info">
              <h5 className="mb-3">🎮 Игра началась!</h5>

              <div className="text-start mb-3">
                <p className="mb-1">1️⃣ Поле сверху — наблюдай за своим путём.</p>
                <p className="mb-1">2️⃣ Внизу — <a href="#chat-section" className="alert-link">чат с Ведущим</a>, туда пиши свой запрос.</p>
                <p className="mb-0">3️⃣ Если что-то непонятно — я рядом, пиши мне в <a
                  href="https://t.me/AlexGo007"
                  target="_blank"
                  rel="noreferrer"
                  className="alert-link"
                >Telegram</a>.</p>
              </div>

              <div className="accordion" id="guestGameInstructionsAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingGuestInstructions">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseGuestInstructions"
                      aria-expanded="false"
                      aria-controls="collapseGuestInstructions"
                    >
                      ℹ️ Как играть?
                    </button>
                  </h2>
                  <div
                    id="collapseGuestInstructions"
                    className="accordion-collapse collapse"
                    aria-labelledby="headingGuestInstructions"
                    data-bs-parent="#guestGameInstructionsAccordion"
                  >
                    <div className="accordion-body text-start">
                      <ul className="mb-3">
                        <li>💬 Весь игровой процесс идёт через чат с Ведущим — поле лишь отражает ваш путь.</li>
                        <li>🎲 Кубик бросается в чате (появится кнопка, когда придёт время).</li>
                        <li>🗺️ Поле всегда сверху страницы, чтобы вы могли наблюдать движение.</li>
                        <li>🎯 Цель игры — раскрыть свой запрос и увидеть, как поле отражает твой путь.</li>
                      </ul>

                      <p className="mb-2"><strong>💡 Как составить запрос:</strong></p>
                      <ul className="mb-3">
                        <li>От конкретной темы — «Отношения», «Деньги», «Самореализация»</li>
                        <li>От чувства или боли — «Боюсь проявляться», «Чувствую застой»</li>
                        <li>С целью — «Запустить свой проект на тему...»</li>
                      </ul>

                      <p className="mb-2"><strong>❌ Не подходит:</strong></p>
                      <p className="mb-0">«Быть счастливым», «Изменить всё сразу»</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {currentCell !== null && (
          <GameBoard
            currentCell={currentCell}
            snakes={SNAKES}
            ladders={LADDERS}
            special={{
              startFinish: 68,
              checks: [69, 70, 71]
            }}
            onCellClick={(cell) => console.log(`Clicked cell ${cell}`)}
          />
        )}

        {gameId && (
          <div id="chat-section" className="mt-4">
            <Chat
              gameId={gameId}
              onCellChange={handleCellChange}
              isGuestMode={true}
              onGuestLimitReached={handleGuestLimitReached}
              onGuestInteractionUpdate={(remaining) => setGuestInteractionsRemaining(remaining)}
              guestInteractionsRemaining={guestInteractionsRemaining}
            />
          </div>
        )}

        <DiceAnimationOverlay
          isVisible={animationState.isDiceRolling}
          stage={animationState.currentStage}
          diceResult={animationState.diceResult}
          onComplete={() => {}}
        />
      </div>
    );
  }

  // LANDING PAGE - полная копия LP/lilla_AI2_rt.html
  return (
    <>
      <style dangerouslySetInnerHTML={{__html: `
        /* Reset */
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
            line-height: 1.7;
            color: #1f2937;
            background: #fafafa;
        }

        /* Container */
        .container {
            max-width: 1100px;
            margin: 0 auto;
            padding: 0 20px;
        }

        .container-narrow {
            max-width: 800px;
            margin: 0 auto;
            padding: 0 20px;
        }

        /* Hero Section */
        .hero {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
            position: relative;
            overflow: hidden;
            padding: 80px 0 60px;
        }

        .hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.96);
            backdrop-filter: blur(8px);
        }

        .hero-content {
            position: relative;
            z-index: 2;
            text-align: center;
        }

        h1 {
            font-size: 3.2rem;
            font-weight: 800;
            color: #1e3a8a;
            margin-bottom: 1.5rem;
            line-height: 1.2;
        }

        .hero-subtitle {
            font-size: 1.4rem;
            color: #374151;
            margin-bottom: 2.5rem;
            line-height: 1.6;
            max-width: 700px;
            margin-left: auto;
            margin-right: auto;
        }

        /* CTA Button */
        .cta-primary {
            background: linear-gradient(135deg, #f59e0b, #f97316);
            color: white;
            border: none;
            padding: 20px 50px;
            font-size: 1.3rem;
            font-weight: 700;
            border-radius: 50px;
            cursor: pointer;
            box-shadow: 0 10px 30px rgba(245, 158, 11, 0.4);
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-block;
        }

        .cta-primary:hover {
            transform: translateY(-3px);
            box-shadow: 0 15px 40px rgba(245, 158, 11, 0.5);
        }

        .cta-secondary {
            background: white;
            color: #1e3a8a;
            border: 2px solid #1e3a8a;
            padding: 18px 45px;
            font-size: 1.2rem;
            font-weight: 600;
            border-radius: 50px;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            display: inline-block;
        }

        .cta-secondary:hover {
            background: #1e3a8a;
            color: white;
        }

        /* Social Proof под CTA */
        .social-proof {
            margin-top: 2rem;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 2rem;
            font-size: 0.95rem;
            color: #6b7280;
        }

        .social-proof-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .social-proof-item span {
            font-weight: 600;
        }

        /* Sections */
        section {
            padding: 60px 0;
        }

        .section-title {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1e3a8a;
            text-align: center;
            margin-bottom: 3rem;
        }

        .section-subtitle {
            font-size: 1.2rem;
            color: #6b7280;
            text-align: center;
            margin-bottom: 3rem;
            max-width: 700px;
            margin-left: auto;
            margin-right: auto;
        }

        /* How It Works - Steps */
        .steps-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 2.5rem;
            max-width: 1000px;
            margin: 0 auto 3rem;
        }

        .step-card {
            text-align: center;
            padding: 2rem 1.5rem;
            background: white;
            border-radius: 20px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
            transition: transform 0.3s ease;
        }

        .step-card:hover {
            transform: translateY(-5px);
        }

        .step-number {
            width: 60px;
            height: 60px;
            background: linear-gradient(135deg, #667eea, #764ba2);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.8rem;
            font-weight: 700;
            margin: 0 auto 1.5rem;
        }

        .step-title {
            font-size: 1.3rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 1rem;
        }

        .step-description {
            font-size: 1rem;
            color: #6b7280;
            line-height: 1.6;
        }

        /* Benefits Cards */
        .benefits-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 2rem;
            margin-bottom: 3rem;
        }

        .benefit-card {
            background: white;
            border-radius: 20px;
            padding: 2.5rem 2rem;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
            text-align: center;
        }

        .benefit-icon {
            font-size: 3rem;
            margin-bottom: 1.5rem;
        }

        .benefit-title {
            font-size: 1.4rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 1rem;
        }

        .benefit-text {
            font-size: 1rem;
            color: #6b7280;
            line-height: 1.7;
        }

        /* Testimonials */
        .testimonials-grid {
            display: grid;
            gap: 2.5rem;
            max-width: 900px;
            margin: 0 auto 3rem;
        }

        .testimonial {
            background: white;
            border-radius: 20px;
            padding: 2.5rem;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
            border-left: 5px solid #667eea;
        }

        .testimonial-text {
            font-size: 1.05rem;
            color: #374151;
            line-height: 1.8;
            margin-bottom: 1.5rem;
            font-style: italic;
        }

        .testimonial-text strong {
            color: #1f2937;
            font-weight: 700;
            font-style: normal;
        }

        .testimonial-author {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding-top: 1.5rem;
            border-top: 1px solid #e5e7eb;
        }

        .author-info {
            flex: 1;
        }

        .author-name {
            font-weight: 700;
            color: #1f2937;
            font-size: 1.1rem;
        }

        .author-meta {
            font-size: 0.9rem;
            color: #9ca3af;
        }

        /* CTA Section */
        .cta-section {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            position: relative;
            overflow: hidden;
        }

        .cta-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.15);
        }

        .cta-content {
            position: relative;
            z-index: 2;
            text-align: center;
            color: white;
        }

        .cta-section h2 {
            color: white;
            font-size: 2.5rem;
            margin-bottom: 1.5rem;
        }

        .cta-section p {
            font-size: 1.3rem;
            margin-bottom: 2.5rem;
            opacity: 0.95;
        }

        /* Footer */
        footer {
            background: #1f2937;
            color: #9ca3af;
            padding: 40px 0;
            text-align: center;
        }

        footer p {
            margin-bottom: 0.5rem;
        }

        footer a {
            color: #60a5fa;
            text-decoration: none;
        }

        footer a:hover {
            text-decoration: underline;
        }

        /* Form styling for guest game creation */
        .guest-form-container {
            max-width: 500px;
            margin: 0 auto 2rem;
            background: white;
            padding: 2rem;
            border-radius: 20px;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .guest-form-input {
            width: 100%;
            padding: 18px 20px;
            font-size: 1.1rem;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            margin-bottom: 1rem;
            transition: border-color 0.3s ease;
        }

        .guest-form-input:focus {
            outline: none;
            border-color: #667eea;
        }

        .guest-form-message {
            margin-top: 1rem;
            padding: 1rem;
            border-radius: 12px;
            font-size: 0.95rem;
        }

        .guest-form-message.error {
            background: #fee2e2;
            color: #991b1b;
        }

        .guest-form-message.success {
            background: #d1fae5;
            color: #065f46;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
            h1 {
                font-size: 2.2rem;
            }

            .hero-subtitle {
                font-size: 1.1rem;
            }

            .cta-primary {
                padding: 18px 40px;
                font-size: 1.1rem;
            }

            .section-title {
                font-size: 2rem;
            }

            .social-proof {
                flex-direction: column;
                gap: 1rem;
            }

            .steps-grid,
            .benefits-grid {
                grid-template-columns: 1fr;
            }
        }
      `}} />

      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Получи ясность по своему главному вопросу за 30 минут</h1>

            <p className="hero-subtitle">
              Не знаешь, куда двигаться дальше? Древняя игра Лила + AI покажет,
              что именно блокирует твой путь — и как сделать первый шаг.
            </p>

            {/* Guest Form */}
            <div className="guest-form-container">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Введите ваше имя"
                className="guest-form-input"
                maxLength={50}
                disabled={isCreating}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isCreating) {
                    handleCreateGuestGame();
                  }
                }}
              />

              <button
                className="cta-primary"
                onClick={handleCreateGuestGame}
                disabled={isCreating || !playerName.trim()}
                style={{ width: '100%' }}
              >
                {isCreating ? 'Создаём ваш путь...' : 'Задать свой вопрос бесплатно'}
              </button>

              {/* Disclaimer под кнопкой */}
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                fontSize: '0.9rem',
                color: '#6b7280',
                lineHeight: '1.6',
                textAlign: 'center',
                background: 'rgba(249, 115, 22, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(249, 115, 22, 0.1)'
              }}>
                🎁 Бесплатная часть — это уже настоящий инструмент для работы с собой.
                Если почувствуете ценность и захотите пройти полный путь — полная игра от €19.
              </div>

              {message && (
                <div className={`guest-form-message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                  {message}
                </div>
              )}
            </div>

            <div className="social-proof">
              <div className="social-proof-item">
                <span>✅</span> 67 человек уже прошли до финала
              </div>
              <div className="social-proof-item">
                <span>💬</span> В среднем 48 сообщений глубокого диалога
              </div>
              <div className="social-proof-item">
                <span>⏱️</span> Без групп и ожидания — начни прямо сейчас
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <h2 className="section-title">Как это работает</h2>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">Задай свой вопрос</h3>
              <p className="step-description">
                Сформулируй запрос — про отношения, работу, деньги, предназначение.
                AI поможет уточнить и углубить.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">Брось кубик и двигайся</h3>
              <p className="step-description">
                68 архетипических клеток — твоя карта. Стрелы поднимают,
                змеи возвращают к урокам.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Диалог с AI</h3>
              <p className="step-description">
                На каждой клетке AI задаёт вопросы, помогает осознать паттерны,
                даёт инсайты под твой запрос.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <h3 className="step-title">Получи карту пути</h3>
              <p className="step-description">
                В конце игры — ясное понимание ситуации и конкретные
                первые шаги для изменений.
              </p>
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button className="cta-secondary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Начать игру бесплатно
            </button>
          </div>
        </div>
      </section>

      <section style={{ background: 'white' }}>
        <div className="container">
          <h2 className="section-title">Почему это работает</h2>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">🚀</div>
              <h3 className="benefit-title">Мгновенный старт</h3>
              <p className="benefit-text">
                Не нужно ждать группу или мастера. Начни прямо сейчас —
                в любое время дня и ночи.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">🔒</div>
              <h3 className="benefit-title">Полная анонимность</h3>
              <p className="benefit-text">
                Никто не узнает твои вопросы и ответы.
                AI не запоминает, не судит, не рассказывает.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">💎</div>
              <h3 className="benefit-title">Персональная работа</h3>
              <p className="benefit-text">
                AI адаптируется под твой запрос на каждой клетке.
                Это не общие советы — это про тебя.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="container">
          <h2 className="section-title">Что говорят те, кто прошёл до финала</h2>

          <div className="testimonials-grid">
            <div className="testimonial">
              <p className="testimonial-text">
                "Пришла с чувством вины — сын не учится, не работает, и я думала, что я плохая мать.
                Игра показала, что я не даю ему права на собственный путь, пытаясь всё контролировать из страха.
                <br /><br />
                <strong>Самый сильный инсайт:</strong> сын дан мне не как наказание, а как источник роста и благословений.
                Это перевернуло всё. Вместо стыда — благодарность. Вместо контроля — доверие Богу и его силе.
                <br /><br />
                Сейчас впервые за годы чувствую лёгкость. Могу просто быть рядом и наслаждаться общением,
                без попыток исправить или спасти."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Елена, 45 лет</div>
                  <div className="author-meta">Психолог • 91 сообщение с AI • ~4 часа</div>
                </div>
              </div>
            </div>

            <div className="testimonial">
              <p className="testimonial-text">
                "30 лет религиозных запретов — 'телесное от дьявола', 'секс грешен'.
                Я пришёл понять, почему не могу построить отношения.
                <br /><br />
                <strong>AI не пытался меня успокоить или переубедить.</strong> Он просто дал пространство злости.
                Я впервые за годы позволил себе чувствовать ненависть к тому, что со мной сделали.
                Это было страшно и освобождающе одновременно.
                <br /><br />
                Инсайт пришёл на клетке 'Самореализация': моё тело — не враг, а союзник.
                Религия отняла 30 лет, но сейчас, прямо сейчас, я могу начать жить по-новому.
                <br /><br />
                После игры: просто сижу, плачу и чувствую. Первый раз за многие годы."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Максим, 35 лет</div>
                  <div className="author-meta">Прошёл 3 игры • ~190 сообщений</div>
                </div>
              </div>
            </div>

            <div className="testimonial">
              <p className="testimonial-text">
                "4 года после развода не могу довериться мужчине. Как только дело доходит до секса — сбегаю.
                Думала, проблема в неправильном выборе партнёров.
                <br /><br />
                <strong>AI задал вопрос:</strong> 'А что, если твоя не-спешка — это мудрость тела, а не страх?'
                Это был прорыв. Я не 'сломана' — я просто учусь различать настоящее и иллюзию.
                <br /><br />
                <strong>Инсайт:</strong> желание научиться любить без страха начинается с принятия своего темпа.
                Мне не нужно торопиться, чтобы кого-то удержать. Настоящее меня подождёт.
                <br /><br />
                Впервые за 4 года чувствую не вину за 'неправильность', а нежность к себе."
              </p>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Ирина, 42 года</div>
                  <div className="author-meta">89 сообщений</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: '900px', margin: '3rem auto 0', padding: '2.5rem', background: '#f8f9fa', borderRadius: '20px' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1f2937', textAlign: 'center', marginBottom: '2rem' }}>
              Что говорят игроки после финала
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Игра с тобой была прекрасной! Твоя мудрость и поддержка меня восхитили и помогли раскрыть мое сердце!"
                </p>
              </div>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Единство с Творцом и с сыном! Чувство благодарности и восхищения Божественной игрой!"
                </p>
              </div>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Впервые за многие годы чувствую надежду — вижу свет вдали между деревьями"
                </p>
              </div>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Качественное осознание: больше не буду делать так, как делал до этого"
                </p>
              </div>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Хочу радостно и счастливо проводить те дни, которые могу, потому что чувствую твою душу"
                </p>
              </div>
              <div style={{ padding: '1.2rem', background: 'white', borderRadius: '12px', borderLeft: '3px solid #667eea' }}>
                <p style={{ fontSize: '0.95rem', color: '#374151', fontStyle: 'italic', margin: 0 }}>
                  "Теперь могу действовать решительно, разяще, мощно, масштабно, зная то, что мне надо"
                </p>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <button className="cta-primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Начать свою игру
            </button>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Твоя очередь получить ясность</h2>
            <p>3 бесплатных хода. Начни прямо сейчас — без регистрации.</p>
            <button
              className="cta-primary"
              style={{ background: 'white', color: '#667eea' }}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Задать свой вопрос
            </button>
            <div className="social-proof" style={{ color: 'white', opacity: 0.9, marginTop: '2rem' }}>
              <div className="social-proof-item">
                <span>✅</span> 515 игр уже сыграно
              </div>
              <div className="social-proof-item">
                <span>⚡</span> В среднем 9+ инсайтов за игру
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer>
        <div className="container">
          <p><strong>Квантовый Перекрёсток</strong> — онлайн-версия древней игры Лила</p>
          <p>Основано на книге Харриша Джохари • AI-наставник Claude Sonnet 4.5</p>
          <p style={{ marginTop: '1.5rem' }}>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a> •
            <a href="/cookies.html" target="_blank" rel="noopener noreferrer">Cookies</a> •
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a> •
            <a href="/contact.html" target="_blank" rel="noopener noreferrer">Contact</a> •
            <a href="/impressum.html" target="_blank" rel="noopener noreferrer">Impressum</a>
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
            © 2025 Quantum Crossroad
          </p>
        </div>
      </footer>
    </>
  );
};

export default GuestGamePage;
